/**
 * ComposeMail — Inline Outlook-style compose panel.
 *
 * Uses a native contentEditable div as the editor so that pasted content
 * (email signatures, tables, inline styles, fonts, spacing) is preserved
 * exactly as the browser sees it — no Quill stripping or conversion.
 *
 * Formatting is applied via document.execCommand() which works natively on
 * any focused contentEditable element.
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Tooltip, message as antMsg, Spin, Dropdown, DatePicker, Modal } from 'antd';
import dayjs from 'dayjs';
import {
  DeleteOutlined, ExclamationCircleOutlined,
  CheckCircleOutlined, LoadingOutlined,
  PaperClipOutlined, CloseOutlined,
  DownOutlined, ExpandAltOutlined,
  FileOutlined, FilePdfOutlined, FileImageOutlined,
  FileWordOutlined, FileExcelOutlined,
  CheckOutlined, MinusCircleOutlined, EyeOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';

import {
  outlookSendThunk,
  outlookReplyThunk,
  outlookReplyAllThunk,
  saveDraftThunk,
  sendDraftThunk,
  deleteDraftThunk,
} from '../../../Slices/Thunk/outlookComposeThunks';
import { createScheduledEmail, addScheduledItem } from '../../../Slices/scheduledMailSlice';
import { clearInlineCompose, pushNotification } from '../../../Slices/mailUiSlice';
import { updateThreadAfterSend } from '../../../Slices/Thunk/graphMailThunks';
import {
  addPendingMessage,
  markPendingSent,
  markPendingFailed,
  addDraftId,
  removeDraftId,
} from '../../../Slices/mailMessagesSlice';
import {
  registerCompose, unregisterCompose,
  pushUndoSnapshot, undoHistory, redoHistory,
} from './composeRegistry';
import { getDefaultSignatureId } from './OutlookSignatureManager';

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  blue:      '#0f6cbd',
  blueDark:  '#115ea3',
  text:      '#242424',
  textSec:   '#605e5c',
  textMuted: '#8a8886',
  border:    '#edebe9',
  bg:        '#ffffff',
  red:       '#c50f1f',
  redBg:     '#fde7e9',
  blueLight: '#eff6fc',
};
const FONT = "'Segoe UI', Tahoma, sans-serif";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function fileSizeLabel(bytes) {
  if (!bytes) return '';
  if (bytes < 1024)    return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

const FILE_ICON_MAP = {
  'application/pdf': <FilePdfOutlined style={{ color: C.red }} />,
  image:             <FileImageOutlined style={{ color: C.blue }} />,
  'application/msword': <FileWordOutlined style={{ color: '#2b579a' }} />,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': <FileWordOutlined style={{ color: '#2b579a' }} />,
  'application/vnd.ms-excel': <FileExcelOutlined style={{ color: '#107c10' }} />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <FileExcelOutlined style={{ color: '#107c10' }} />,
};
function getFileIcon(type = '') {
  if (FILE_ICON_MAP[type]) return FILE_ICON_MAP[type];
  if (type.startsWith('image')) return FILE_ICON_MAP['image'];
  return <FileOutlined style={{ color: C.textMuted }} />;
}

function emailTokenIsValid(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}


// ─────────────────────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────────────────────
const schema = Yup.object({
  to:      Yup.array().of(Yup.string().email()).min(1, 'At least one recipient required'),
  cc:      Yup.array().of(Yup.string().email()),
  bcc:     Yup.array().of(Yup.string().email()),
  subject: Yup.string(),
  body:    Yup.string(),
});

// ─────────────────────────────────────────────────────────────────────────────
// RecipientField — Outlook-style inline email input row
// ─────────────────────────────────────────────────────────────────────────────
function RecipientField({ label, field, formik, placeholder, rightSlot }) {
  const value = formik.values[field] || [];
  const error = formik.submitCount > 0 && formik.errors[field];

  return (
    <div style={s.fieldRow}>
      <span style={s.fieldLabel}>{label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Select
          mode="tags"
          style={{ width: '100%', fontFamily: FONT }}
          variant="borderless"
          value={value}
          placeholder={placeholder}
          onChange={vals => formik.setFieldValue(field, vals)}
          onBlur={formik.handleBlur}
          tokenSeparators={[',', ';', ' ']}
          tagRender={({ label: lbl, closable, onClose }) => {
            const valid = emailTokenIsValid(String(lbl));
            return (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '1px 8px', margin: '2px 3px', borderRadius: 12,
                background: valid ? C.blueLight : C.redBg,
                border: `1px solid ${valid ? C.blue : C.red}`,
                color: valid ? C.blue : C.red,
                fontSize: 13, fontFamily: FONT,
              }}>
                {lbl}
                {closable && (
                  <CloseOutlined
                    style={{ fontSize: 9, cursor: 'pointer', marginLeft: 2 }}
                    onClick={onClose}
                  />
                )}
              </span>
            );
          }}
          styles={{ popup: { root: { fontFamily: FONT } } }}
          popupMatchSelectWidth={false}
          notFoundContent={null}
          suffixIcon={null}
          open={false}
        />
        {error && (
          <span style={{ fontSize: 11, color: C.red, fontFamily: FONT, paddingLeft: 2 }}>
            {error}
          </span>
        )}
      </div>
      {rightSlot}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AttachmentItem
// ─────────────────────────────────────────────────────────────────────────────
function AttachmentItem({ file, onRemove }) {
  return (
    <div style={s.attChip}>
      {getFileIcon(file.type)}
      <span style={s.attName}>{file.name}</span>
      <span style={s.attSize}>{fileSizeLabel(file.size)}</span>
      <button style={s.attRemove} onClick={() => onRemove(file.uid)}>
        <CloseOutlined style={{ fontSize: 10 }} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ComposeMail
// ─────────────────────────────────────────────────────────────────────────────
  
// ─────────────────────────────────────────────────────────────────────────────
// Dual-zone editor DOM builders
//
// The editor is split into two explicit, marked zones:
//
//   [data-compose-zone="user"]    ← contentEditable — user types here
//   [data-compose-zone="history"] ← NOT contentEditable — display only, read-only
//
// This is the structural contract:
//   • API payload  = innerHTML of the user zone only
//   • Display HTML = full editor innerHTML (both zones)
//   • The history zone cannot be accidentally edited, cursor-jumped into,
//     or formatted by the ribbon (applyStyle targets only the focused node
//     which cannot be inside a non-editable area)
//
// Zone constants — used both here and in getPayloadHtml / getDisplayHtml
// ─────────────────────────────────────────────────────────────────────────────

const ZONE_USER    = 'data-compose-zone="user"';
const ZONE_HISTORY = 'data-compose-zone="history"';

/**
 * Build the full editor innerHTML for a reply or replyAll.
 * User zone: empty paragraph (cursor lands here).
 * History zone: non-editable blockquote of the original message.
 */
function buildReplyEditorHtml(originalBody) {
  return (
    `<div ${ZONE_USER} style="min-height:1.4em;outline:none;"><p><br></p></div>` +
    `<div ${ZONE_HISTORY} contenteditable="false" style="margin-top:12px;padding-top:8px;border-top:1px solid #edebe9;color:#605e5c;">` +
      `<blockquote style="border-left:3px solid #c8c6c4;padding-left:12px;margin:8px 0;">${originalBody}</blockquote>` +
    `</div>`
  );
}

/**
 * Build the full editor innerHTML for a forward.
 * User zone: empty paragraph.
 * History zone: non-editable header + original body.
 */
function buildForwardEditorHtml(originalBody, originalSubject, originalMessage) {
  const from    = originalMessage?.from;
  const fromStr = from?.name
    ? `${from.name} &lt;${from.email || ''}&gt;`
    : (from?.email || '');
  const sentRaw  = originalMessage?.sentDateTime || originalMessage?.receivedDateTime;
  const sentStr  = sentRaw
    ? new Date(sentRaw).toLocaleString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long',
        day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : '';
  const toStr = (originalMessage?.toRecipients || [])
    .map(r => r.name ? `${r.name} &lt;${r.email}&gt;` : r.email)
    .join('; ');

  return (
    `<div ${ZONE_USER} style="min-height:1.4em;outline:none;"><p><br></p></div>` +
    `<div ${ZONE_HISTORY} contenteditable="false" style="margin-top:16px;font-family:Calibri,sans-serif;font-size:11pt;color:#000;">` +
      `<p style="margin:0;"><br></p>` +
      (fromStr ? `<p style="margin:0;"><b>From:</b> ${fromStr}</p>` : '') +
      (sentStr ? `<p style="margin:0;"><b>Sent:</b> ${sentStr}</p>`  : '') +
      (toStr   ? `<p style="margin:0;"><b>To:</b> ${toStr}</p>`      : '') +
      `<p style="margin:0;"><b>Subject:</b> ${originalSubject || ''}</p>` +
      `<p style="margin:0;"><br></p>` +
      `<div>${originalBody}</div>` +
    `</div>`
  );
}

/**
 * Extract ONLY the user-authored content from the editor.
 *
 * Reads the [data-compose-zone="user"] child of the editor element.
 * If the zone is not present (new email, popout transfer, or legacy HTML),
 * falls back to the full innerHTML — which is correct because new/popout
 * editors have no history zone.
 *
 * This is the authoritative API payload extractor.
 * No regex, no querySelectorAll('blockquote') guessing.
 */
function getPayloadHtml(editorEl) {
  if (!editorEl) return '';
  const userZone = editorEl.querySelector('[data-compose-zone="user"]');
  if (userZone) return userZone.innerHTML;
  // No zone structure → editor is a plain new-email editor, send everything
  return editorEl.innerHTML;
}

/**
 * Get the full display HTML (both zones) — used for popout transfer.
 * Preserves exact editor state so the popout window shows identical content.
 */
function getDisplayHtml(editorEl) {
  if (!editorEl) return '';
  return editorEl.innerHTML;
}

export default function ComposeMail({
  composeType    = 'new',
  initialValues  = {},
  onClose,
  onSent,
  onPopout,
  composeOptions = {},
}) {
  const dispatch    = useDispatch();
  const loginUser   = useSelector(s => s?.loginUserInfo?.loginUserDetails);
  const signatures  = useSelector(s => s?.sales?.signatures || []);
  const editorRef   = useRef(null);   // points to the contentEditable div
  const imgRef    = useRef(null);
  const attachRef = useRef(null);

  const [showBcc,             setShowBcc]             = useState(
    composeType === 'editDraft' ? (initialValues.bcc?.length > 0) : false
  );
  const [fileList,            setFileList]            = useState([]);
  const [sendStatus,          setSendStatus]          = useState('idle');
  const [sendError,           setSendError]           = useState('');
  const [editorEmpty,         setEditorEmpty]         = useState(true);
  const [subjectConfirm,      setSubjectConfirm]      = useState(false);
  const pendingValuesRef = useRef(null);

  // ── Draft lifecycle state ────────────────────────────────────────────────
  // Only active for composeType === 'new'. Reply/forward use Graph's built-in
  // reply endpoints which don't need an explicit draft round-trip.
  const [draftStatus, setDraftStatus] = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const draftIdRef        = useRef(initialValues.draftId || null);
  const isDirtyRef        = useRef(false);   // true when content changed since last save
  const savingRef         = useRef(false);   // guard against concurrent saves
  const formikRef         = useRef(null);    // always-current formik reference
  const snapshotTimerRef  = useRef(null);    // debounce handle for undo snapshots

  // ── Send options (More send options dropdown) ────────────────────────────
  const [sendImportance,        setSendImportance]        = useState('normal');
  const [sendReadReceipt,       setSendReadReceipt]       = useState(false);
  const [sendSensitivity,       setSendSensitivity]       = useState('normal');
  const [scheduledSendDateTime, setScheduledSendDateTime] = useState(null); // ISO string or null
  const [schedulePickerOpen,    setSchedulePickerOpen]    = useState(false);
  const [pickerValue,           setPickerValue]           = useState(null);  // dayjs value

  // Sync from ribbon Options tab whenever composeOptions changes
  useEffect(() => {
    if (composeOptions.importance  !== undefined) setSendImportance(composeOptions.importance);
    if (composeOptions.requestReadReceipt !== undefined) setSendReadReceipt(composeOptions.requestReadReceipt);
    if (composeOptions.sensitivity !== undefined) setSendSensitivity(composeOptions.sensitivity);
    if (composeOptions.showBcc     !== undefined) setShowBcc(composeOptions.showBcc);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeOptions]);

  // Returns the contentEditable DOM node
  const getEditor = useCallback(() => editorRef.current, []);

  // ── Formik ──────────────────────────────────────────────────────────────
  const subjectPrefix =
    composeType === 'reply'    ? 'RE: ' :
    composeType === 'replyAll' ? 'RE: ' :
    composeType === 'forward'  ? 'FW: ' : '';

  const formik = useFormik({
    initialValues: {
      to:      initialValues.to      || [],
      cc:      initialValues.cc      || [],
      bcc:     initialValues.bcc     || [],
      subject: initialValues.subject
        ? (composeType === 'editDraft'
            ? initialValues.subject  // draft: preserve exact subject, no RE:/FW: prefix
            : subjectPrefix + initialValues.subject.replace(/^(RE:|FW:|Fwd:)\s*/i, ''))
        : '',
      body: '',
    },
    validationSchema: schema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: handleSend,
  });

  // ── Mount: inject zone-structured HTML into editor ──────────────────────
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    let html = '';

    if (initialValues.prefillBody) {
      // Popout transfer — the inline editor already had zone structure, preserve it exactly
      html = initialValues.prefillBody;
    } else if (composeType === 'reply' || composeType === 'replyAll') {
      // Zone structure: user zone (empty, editable) + history zone (non-editable blockquote)
      // Graph /reply appends the original automatically — we only send the user zone payload
      html = buildReplyEditorHtml(initialValues.body || '');
    } else if (composeType === 'forward') {
      // Zone structure: user zone (empty, editable) + history zone (non-editable original body)
      // Forward uses createForward draft API which also appends original — user zone only sent
      html = buildForwardEditorHtml(initialValues.body || '', initialValues.subject || '', initialValues.originalMessage);
    } else if (composeType === 'editDraft') {
      // Restore saved draft HTML — no zone structure, just the raw saved content
      html = initialValues.body || '';
    } else if (composeType === 'new') {
      // New email: no history zone, just a signature if one is set as default
      const empId  = loginUser?.id || '';
      const defId  = getDefaultSignatureId(empId);
      const defSig = defId ? signatures.find(s => s.id === defId) : null;
      if (defSig) {
        html = `<p><br></p><div class="bpa-signature" style="border-top:1px solid #edebe9;margin-top:12px;padding-top:10px;">${defSig.content}</div>`;
      }
    }

    if (html) {
      el.innerHTML = html;
      setEditorEmpty(false);
    }

    // Place cursor at the start of the user zone (or editor root for new emails)
    try {
      const userZone = el.querySelector('[data-compose-zone="user"]') || el;
      const sel = window.getSelection();
      const range = document.createRange();
      range.setStart(userZone, 0);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (_) { /* ignore if editor not yet in DOM */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep formikRef current so callbacks that only run periodically see latest values
  formikRef.current = formik;

  // ── Draft helpers ────────────────────────────────────────────────────────
  // Only wired for composeType === 'new'. Reply/forward use Graph reply endpoints.

  /** Build Graph-shaped draft payload from current formik + editor state. */
  const buildDraftPayload = useCallback(() => ({
    to:       formikRef.current.values.to,
    cc:       formikRef.current.values.cc,
    bcc:      formikRef.current.values.bcc,
    subject:  formikRef.current.values.subject,
    bodyHtml: getPayloadHtml(editorRef.current),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  /**
   * Save (create or update) the current draft.
   * Exposed via registry so the ribbon Save Draft button reaches it.
   * Returns the draftId string (new or existing).
   */
  const triggerSaveRef = useRef(null);
  const triggerSave = useCallback(async () => {
    if (composeType !== 'new' && composeType !== 'editDraft') return null;
    if (savingRef.current) return draftIdRef.current;
    savingRef.current = true;
    setDraftStatus('saving');
    try {
      const result = await dispatch(saveDraftThunk({
        draftId: draftIdRef.current,
        ...buildDraftPayload(),
      })).unwrap();

      const newId = result?.draftId || null;
      if (newId && !draftIdRef.current) {
        draftIdRef.current = newId;
        dispatch(addDraftId({ messageId: newId }));
      }
      isDirtyRef.current = false;
      setDraftStatus('saved');
      return draftIdRef.current;
    } catch {
      setDraftStatus('error');
      return draftIdRef.current;
    } finally {
      savingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, buildDraftPayload, composeType]);

  // Keep triggerSaveRef in sync so the interval always calls the latest closure
  triggerSaveRef.current = triggerSave;

  // ── Dirty tracking: formik field changes ─────────────────────────────────
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; } // skip initial mount
    if (composeType === 'new') isDirtyRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  // ── Auto-save interval (30 s) ─────────────────────────────────────────────
  useEffect(() => {
    if (composeType !== 'new' && composeType !== 'editDraft') return;
    const timer = setInterval(() => {
      if (isDirtyRef.current && !savingRef.current) {
        triggerSaveRef.current?.();
      }
    }, 30_000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Close: save draft then close ─────────────────────────────────────────
  const handleClose = useCallback(async () => {
    if ((composeType === 'new' || composeType === 'editDraft') && isDirtyRef.current) {
      await triggerSaveRef.current?.(); // persist unsaved changes before closing
    }
    dispatch(clearInlineCompose());
    onClose?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onClose, composeType]);

  // ── Discard: delete draft then close (no save) ────────────────────────────
  const handleDiscard = useCallback(async () => {
    if (draftIdRef.current) {
      try { await dispatch(deleteDraftThunk({ draftId: draftIdRef.current })).unwrap(); } catch {}
      dispatch(removeDraftId({ messageId: draftIdRef.current }));
      draftIdRef.current = null;
    }
    isDirtyRef.current = false;
    dispatch(clearInlineCompose());
    onClose?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onClose]);

  // ── Register with composeRegistry ────────────────────────────────────────
  useEffect(() => {
    registerCompose({
      getEditor,
      submitForm:    () => formikRef.current.submitForm(),
      handleClose,
      handleDiscard,
      saveDraft:     () => triggerSaveRef.current?.(),
      triggerAttach: () => attachRef.current?.click(),
    });
    return () => unregisterCompose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, handleDiscard]);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')                              handleClose();
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter')  formikRef.current.submitForm();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose]);

  // ── Editor input handler ─────────────────────────────────────────────────
  const handleEditorInput = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    // Empty detection must check ONLY the user zone.
    // The history zone (quoted reply / forward body) always has content —
    // checking the full editor would make the placeholder never show.
    const userZone = el.querySelector('[data-compose-zone="user"]') || el;
    const isEmpty  = userZone.innerText.trim() === '' && !userZone.querySelector('img, table');
    setEditorEmpty(isEmpty);

    // Mark dirty so auto-save picks it up
    if (composeType === 'new' || composeType === 'editDraft') isDirtyRef.current = true;

    // Debounced undo snapshot — coalesces rapid keystrokes into one undo step.
    // pushUndoSnapshot is a no-op if innerHTML hasn't changed (e.g., during
    // programmatic undo/redo restores that dispatch a synthetic 'input' event).
    clearTimeout(snapshotTimerRef.current);
    snapshotTimerRef.current = setTimeout(() => pushUndoSnapshot(), 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Inline image ─────────────────────────────────────────────────────────
  const handleImageFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      document.execCommand('insertImage', false, ev.target.result);
      formik.setFieldValue('body', el.innerHTML, false);
      setEditorEmpty(false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Attachment files ─────────────────────────────────────────────────────
  const handleAttachFiles = useCallback((e) => {
    [...(e.target.files || [])].forEach(f => {
      if (f.size > 25 * 1024 * 1024) { antMsg.error(`${f.name} exceeds 25 MB`); return; }
      setFileList(prev => [...prev, Object.assign(f, { uid: `${f.name}-${Date.now()}` })]);
    });
    e.target.value = '';
  }, []);

  // ── Send gate: validates subject before proceeding ───────────────────────
  function handleSend(values) {
    if (!values.to.length) {
      formik.setFieldError('to', 'At least one recipient required');
      return;
    }
    if (!values.subject?.trim()) {
      // Stash values and show confirmation — do NOT send yet
      pendingValuesRef.current = values;
      setSubjectConfirm(true);
      return;
    }
    executeSend(values);
  }

  // ── Actual send logic (called after subject confirmed or subject present) ─
  async function executeSend(values) {
    setSendStatus('sending');
    setSendError('');

    const payloadHtml = getPayloadHtml(editorRef.current) || values.body || '';
    const isThreaded = composeType === 'reply' || composeType === 'replyAll' || composeType === 'forward';

    // ── Step 1: optimistic pending message (reply/forward only) ─────────────
    // New emails go to Sent Items which isn't visible in the current thread.
    // Only threaded operations need an optimistic entry in the thread view.
    const clientId = isThreaded && initialValues.conversationId
      ? (crypto.randomUUID?.() || `pending_${Date.now()}_${Math.random().toString(36).slice(2)}`)
      : null;

    const sentAt = new Date().toISOString();
    const fromEmail = initialValues.fromEmail || loginUser?.email || '';
    const fromName  = loginUser?.name || fromEmail;

    if (clientId) {
      const plainPreview = payloadHtml.replace(/<[^>]+>/g, '').slice(0, 255);
      dispatch(addPendingMessage({
        clientId,
        message: {
          clientId,
          conversationId:   initialValues.conversationId,
          folderId:         initialValues.folderId || null,
          from:             { email: fromEmail, name: fromName },
          toRecipients:     values.to.map(e => ({ email: e, name: e })),
          ccRecipients:     values.cc.map(e => ({ email: e, name: e })),
          bccRecipients:    [],
          subject:          values.subject,
          bodyPreview:      plainPreview,
          // Pending card shows only the user's new content — not the full thread history
          body:             { content: payloadHtml, contentType: 'html', loaded: true, blockedImageCount: 0, imagesUnblocked: false },
          bodyStatus:       'loaded',
          hasAttachments:   fileList.length > 0,
          attachments:      [],
          attachmentsLoaded: true,
          receivedDateTime: sentAt,
          sentDateTime:     sentAt,
          sentAt,                    // reconciliation key
          fromEmail,                 // reconciliation key
          isRead:           true,
          isDraft:          false,
          flag:             { flagStatus: 'notFlagged' },
          importance:       'normal',
          categories:       [],
        },
      }));
    }

    try {
      // payloadHtml = user zone only.
      // Graph /reply and /replyAll append the original message thread automatically.
      // Graph /createForward also appends the original. Never send history zone to API.
      if (composeType === 'reply') {
        await dispatch(outlookReplyThunk({
          messageId:          initialValues.replyToMessageId,
          comment:            payloadHtml,
          toRecipients:       values.to,
          fileList,
          requestReadReceipt: sendReadReceipt,
        })).unwrap();
      } else if (composeType === 'replyAll') {
        await dispatch(outlookReplyAllThunk({
          messageId:          initialValues.replyToMessageId,
          comment:            payloadHtml,
          fileList,
          requestReadReceipt: sendReadReceipt,
        })).unwrap();
      } else if (composeType === 'forward') {
        // Use sendMail with full display HTML (user zone + From/Sent/To/Subject history block).
        // Graph's /forward endpoint auto-appends "Forwarded message below:" which we do NOT want.
        const fullForwardHtml = getDisplayHtml(editorRef.current) || values.body || '';
        await dispatch(outlookSendThunk({
          to:                 values.to,
          cc:                 values.cc,
          bcc:                values.bcc,
          subject:            values.subject,
          bodyHtml:           fullForwardHtml,
          fileList,
          importance:         sendImportance,
          sensitivity:        sendSensitivity,
          requestReadReceipt: sendReadReceipt,
        })).unwrap();
      } else {
        // ── New email ────────────────────────────────────────────────────────
        if (scheduledSendDateTime) {
          // Scheduled path: save draft (no /send), register with backend scheduler.
          // The graphScheduledEmailCron will call /messages/{draftId}/send at scheduledAt.
          const result = await dispatch(saveDraftThunk({
            draftId:            draftIdRef.current,
            ...buildDraftPayload(),
            fileList,
            importance:         sendImportance,
            sensitivity:        sendSensitivity,
            requestReadReceipt: sendReadReceipt,
          })).unwrap();
          const activeDraftId = draftIdRef.current || result?.draftId;
          draftIdRef.current  = activeDraftId;

          const userEmail = initialValues.fromEmail || loginUser?.email || '';
          const record = await dispatch(createScheduledEmail({
            userEmail,
            draftId:     activeDraftId,
            scheduledAt: scheduledSendDateTime,
            subject:     values.subject,
            to:          values.to,
            cc:          values.cc,
            bcc:         values.bcc,
          })).unwrap();
          dispatch(addScheduledItem({ ...record, status: 'pending' }));
        } else if (draftIdRef.current) {
          // Draft exists — update + send
          await dispatch(saveDraftThunk({
            draftId:            draftIdRef.current,
            ...buildDraftPayload(),
            fileList,
            importance:         sendImportance,
            sensitivity:        sendSensitivity,
            requestReadReceipt: sendReadReceipt,
          })).unwrap();
          await dispatch(sendDraftThunk({ draftId: draftIdRef.current })).unwrap();
        } else {
          // Normal immediate send
          await dispatch(outlookSendThunk({
            to:                 values.to,
            cc:                 values.cc,
            bcc:                values.bcc,
            subject:            values.subject,
            bodyHtml:           payloadHtml,
            fileList,
            importance:         sendImportance,
            sensitivity:        sendSensitivity,
            requestReadReceipt: sendReadReceipt,
          })).unwrap();
        }
      }

      // ── Step 2: mark pending as 'sent' → enables reconciliation ───────────
      // Now the status transitions 'sending' → 'sent'. Reconciliation only
      // fires for 'sent' entries, preventing premature removal if the API
      // call hadn't returned yet when a delta sync happened to arrive.
      if (clientId) {
        dispatch(markPendingSent({ clientId }));
      }

      // ── Step 3: remove draft tracking after successful send ───────────────
      if (draftIdRef.current) {
        dispatch(removeDraftId({ messageId: draftIdRef.current }));
        draftIdRef.current = null;
        }
      isDirtyRef.current = false;

      // ── Step 4: update thread summary metadata (no fake message injected) ─
      if (isThreaded && initialValues.conversationId) {
        dispatch(updateThreadAfterSend({
          conversationId: initialValues.conversationId,
          sentAt,
          fromEmail,
          fromName,
          bodyPreview: payloadHtml.replace(/<[^>]+>/g, '').slice(0, 255),
        }));
      }

      setSendStatus('success');
      const successMsg = scheduledSendDateTime
        ? `Scheduled for ${dayjs(scheduledSendDateTime).format('MMM D, h:mm A')}`
        : 'Message sent';
      dispatch(pushNotification({ type: 'success', message: successMsg, autoDismissMs: 4000 }));
      setScheduledSendDateTime(null);
      dispatch(clearInlineCompose());
      setTimeout(() => { setSendStatus('idle'); onSent?.(); onClose?.(); }, 800);

    } catch (err) {
      // ── Step 4: mark pending as failed — stays visible with retry indicator
      if (clientId) {
        dispatch(markPendingFailed({ clientId }));
      }
      setSendStatus('error');
      const msg = err?.message || 'Failed to send.';
      setSendError(msg);
      antMsg.error(msg);
    }
  }

  // ── Pop-out handler ──────────────────────────────────────────────────────
  const handlePopout = useCallback(() => {
    if (!onPopout) return;
    // Transfer the full display HTML (both zones) so the popout window shows
    // the same zone structure and the user can keep editing in the same state.
    const currentBody = getDisplayHtml(editorRef.current) || formik.values.body || '';
    onPopout({
      composeType,
      initialValues: {
        ...initialValues,
        to:          formik.values.to,
        cc:          formik.values.cc,
        bcc:         formik.values.bcc,
        subject:     formik.values.subject,
        prefillBody: currentBody,
      },
      fileList,
    });
    // Close inline compose — popup takes over
    handleClose();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPopout, formik.values, fileList, composeType, initialValues]);

  const isSending  = sendStatus === 'sending';
  const totalSize  = fileList.reduce((n, f) => n + (f.size || 0), 0);
  const fromEmail  = initialValues.fromEmail || '';

  // ── More send options dropdown ───────────────────────────────────────────
  const LABEL_STYLE = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontFamily: FONT, minWidth: 200 };
  const CHECK_STYLE = { marginLeft: 'auto', color: C.blue, fontSize: 12 };

  const moreSendItems = [
    {
      key: 'importance-high',
      label: (
        <span style={LABEL_STYLE}>
          <ExclamationCircleOutlined style={{ color: '#c50f1f' }} />
          High importance
          {sendImportance === 'high' && <CheckOutlined style={CHECK_STYLE} />}
        </span>
      ),
      onClick: () => setSendImportance(v => v === 'high' ? 'normal' : 'high'),
    },
    {
      key: 'importance-low',
      label: (
        <span style={LABEL_STYLE}>
          <MinusCircleOutlined style={{ color: C.textSec }} />
          Low importance
          {sendImportance === 'low' && <CheckOutlined style={CHECK_STYLE} />}
        </span>
      ),
      onClick: () => setSendImportance(v => v === 'low' ? 'normal' : 'low'),
    },
    { type: 'divider' },
    {
      key: 'readreceipt',
      label: (
        <span style={LABEL_STYLE}>
          <EyeOutlined style={{ color: C.textSec }} />
          Request read receipt
          {sendReadReceipt && <CheckOutlined style={CHECK_STYLE} />}
        </span>
      ),
      onClick: () => setSendReadReceipt(v => !v),
    },
    { type: 'divider' },
    {
      key: 'schedule',
      label: (
        <span style={LABEL_STYLE}>
          <CalendarOutlined style={{ color: C.blue }} />
          Schedule send
          {scheduledSendDateTime && <CheckOutlined style={CHECK_STYLE} />}
        </span>
      ),
      onClick: () => {
        setPickerValue(scheduledSendDateTime ? dayjs(scheduledSendDateTime) : dayjs().add(1, 'hour').startOf('hour'));
        setSchedulePickerOpen(true);
      },
    },
    ...(scheduledSendDateTime ? [{
      key: 'clear-schedule',
      label: (
        <span style={{ ...LABEL_STYLE, color: C.red }}>
          <CloseOutlined style={{ color: C.red }} />
          Clear scheduled time
        </span>
      ),
      onClick: () => setScheduledSendDateTime(null),
    }] : []),
  ];

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>

      {/* ── Missing subject confirmation dialog ── */}
      {subjectConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.35)',
        }}>
          <div style={{
            background: '#fff', borderRadius: 6,
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            width: 380, padding: '28px 28px 20px',
            fontFamily: "'Segoe UI', Tahoma, sans-serif",
          }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#242424', marginBottom: 10 }}>
              Send without subject?
            </div>
            <div style={{ fontSize: 13, color: '#444', marginBottom: 24, lineHeight: 1.5 }}>
              This message has no subject. Do you still want to send it?
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => {
                  setSubjectConfirm(false);
                  pendingValuesRef.current = null;
                }}
                style={{
                  padding: '6px 18px', borderRadius: 4, border: '1px solid #d1d1d1',
                  background: '#fff', color: '#242424', fontSize: 13, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Don't send
              </button>
              <button
                onClick={() => {
                  const values = pendingValuesRef.current;
                  pendingValuesRef.current = null;
                  setSubjectConfirm(false);
                  executeSend(values);
                }}
                style={{
                  padding: '6px 18px', borderRadius: 4, border: 'none',
                  background: '#0f6cbd', color: '#fff', fontSize: 13,
                  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Schedule send picker modal ── */}
      <Modal
        open={schedulePickerOpen}
        title="Schedule Send"
        onCancel={() => setSchedulePickerOpen(false)}
        onOk={() => {
          if (pickerValue) {
            setScheduledSendDateTime(pickerValue.toISOString());
          }
          setSchedulePickerOpen(false);
        }}
        okText="Schedule"
        okButtonProps={{ disabled: !pickerValue || pickerValue.isBefore(dayjs()) }}
        width={340}
        destroyOnClose
      >
        <div style={{ padding: '12px 0 4px', fontFamily: FONT }}>
          <div style={{ fontSize: 12, color: C.textSec, marginBottom: 10 }}>
            Choose when to send this message:
          </div>
          <DatePicker
            showTime={{ format: 'HH:mm', minuteStep: 1 }}
            format="MMM D, YYYY h:mm A"
            value={pickerValue}
            onChange={setPickerValue}
            disabledDate={d => d && d.isBefore(dayjs().startOf('day'))}
            showNow={false}
            style={{ width: '100%' }}
            placeholder="Select date and time"
            getPopupContainer={trigger => trigger.parentElement}
          />
          {pickerValue && pickerValue.isBefore(dayjs()) && (
            <div style={{ fontSize: 11, color: C.red, marginTop: 6 }}>
              Please select a future date and time.
            </div>
          )}
        </div>
      </Modal>

      {/* Hidden file inputs */}
      <input type="file" accept="image/*" ref={imgRef}    style={{ display: 'none' }} onChange={handleImageFile} />
      <input type="file" multiple         ref={attachRef} style={{ display: 'none' }} onChange={handleAttachFiles} />

      <div style={s.root}>

        {/* ── Action bar (Send | From | right icons) ── */}
        <div style={s.actionBar}>

          {/* Send button + more-options dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
            <button
              type="button"
              style={{
                ...s.sendBtn,
                borderRadius: '4px 0 0 4px',
                ...(isSending               ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                ...(sendStatus === 'success' ? { background: '#107c10' } : {}),
              }}
              onClick={() => formik.submitForm()}
              disabled={isSending}
              title="Send (Ctrl+Enter)"
              className="cm-send-main"
            >
              {isSending ? (
                <Spin indicator={<LoadingOutlined spin style={{ fontSize: 13, color: '#fff' }} />} />
              ) : sendStatus === 'success' ? (
                <CheckCircleOutlined />
              ) : scheduledSendDateTime ? (
                <CalendarOutlined />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} />
              )}
              <span style={{ marginLeft: 6 }}>
                {isSending ? 'Sending…' : sendStatus === 'success' ? 'Sent!' : scheduledSendDateTime ? 'Schedule' : 'Send'}
              </span>
            </button>
            <Dropdown
              menu={{ items: moreSendItems }}
              trigger={['click']}
              placement="bottomRight"
              disabled={isSending}
            >
              <button
                type="button"
                style={{
                  ...s.sendBtn,
                  width: 28, minWidth: 28, padding: 0,
                  borderLeft: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '0 4px 4px 0',
                  ...(isSending ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                }}
                title="More send options"
                disabled={isSending}
              >
                <DownOutlined style={{ fontSize: 11 }} />
              </button>
            </Dropdown>
          </div>

          {/* Active-option badges */}
          {(sendImportance !== 'normal' || sendReadReceipt || scheduledSendDateTime) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              {sendImportance === 'high' && (
                <span style={s.badge}>
                  <ExclamationCircleOutlined style={{ color: C.red, fontSize: 11 }} /> High
                </span>
              )}
              {sendImportance === 'low' && (
                <span style={s.badge}>
                  <MinusCircleOutlined style={{ color: C.textSec, fontSize: 11 }} /> Low
                </span>
              )}
              {sendReadReceipt && (
                <span style={s.badge}>
                  <EyeOutlined style={{ color: C.textSec, fontSize: 11 }} /> Receipt
                </span>
              )}
              {scheduledSendDateTime && (
                <span style={{ ...s.badge, color: C.blue, borderColor: C.blue, cursor: 'pointer' }}
                  onClick={() => setScheduledSendDateTime(null)}
                  title="Click to clear scheduled time"
                >
                  <CalendarOutlined style={{ fontSize: 11 }} />
                  {' '}{dayjs(scheduledSendDateTime).format('MMM D, h:mm A')}
                  {' '}<CloseOutlined style={{ fontSize: 9 }} />
                </span>
              )}
            </div>
          )}

          {/* From */}
          {fromEmail && (
            <span style={s.fromLabel}>
              From: <strong style={{ fontWeight: 600 }}>{fromEmail}</strong>
            </span>
          )}

          {/* Draft save status (new email only) */}
          {composeType === 'new' && draftStatus !== 'idle' && (
            <span style={{ fontSize: 11, color: C.textMuted, fontFamily: FONT, flexShrink: 0 }}>
              {draftStatus === 'saving' ? 'Saving draft…' :
               draftStatus === 'saved'  ? 'Draft saved'   :
               draftStatus === 'error'  ? 'Save failed'   : ''}
            </span>
          )}

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Right icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Attach file">
              <button style={s.actionIconBtn} className="cm-action-icon" onClick={() => attachRef.current?.click()}>
                <PaperClipOutlined />
              </button>
            </Tooltip>
            {onPopout && (
              <Tooltip title="Open in new window">
                <button style={s.actionIconBtn} className="cm-action-icon" onClick={handlePopout}>
                  <ExpandAltOutlined />
                </button>
              </Tooltip>
            )}
            <Tooltip title="Discard draft (Esc)">
              <button style={s.actionIconBtn} className="cm-action-icon cm-discard" onClick={handleClose}>
                <DeleteOutlined />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* ── Recipient fields ── */}
        <div style={s.fieldsArea}>

          <RecipientField
            label="To"
            field="to"
            formik={formik}
            placeholder="Add recipients"
            rightSlot={
              <button
                style={s.bccToggle}
                onClick={() => setShowBcc(v => !v)}
                title="Show Bcc"
              >
                Bcc
              </button>
            }
          />

          <RecipientField label="Cc" field="cc" formik={formik} placeholder="" />

          {showBcc && (
            <RecipientField label="Bcc" field="bcc" formik={formik} placeholder="" />
          )}

          <div style={{ ...s.fieldRow, alignItems: 'center' }}>
            <input
              name="subject"
              value={formik.values.subject}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Add a subject"
              style={s.subjectInput}
              autoComplete="off"
            />
            {sendStatus === 'error' && sendError && (
              <span style={{ fontSize: 11, color: C.red, fontFamily: FONT, paddingRight: 12, flexShrink: 0 }}>
                <ExclamationCircleOutlined style={{ marginRight: 3 }} />{sendError}
              </span>
            )}
          </div>

          {formik.submitCount > 0 && formik.errors.to && (
            <div style={{ padding: '3px 14px', fontSize: 12, color: C.red, fontFamily: FONT }}>
              <ExclamationCircleOutlined style={{ marginRight: 4 }} />{formik.errors.to}
            </div>
          )}
        </div>

        {/* ── Attachments strip ── */}
        {fileList.length > 0 && (
          <div style={s.attSection}>
            {fileList.map(f => (
              <AttachmentItem
                key={f.uid}
                file={f}
                onRemove={uid => setFileList(prev => prev.filter(x => x.uid !== uid))}
              />
            ))}
            <span style={s.attTotal}>
              {fileList.length} file{fileList.length > 1 ? 's' : ''} · {fileSizeLabel(totalSize)}
            </span>
          </div>
        )}

        {/* ── contentEditable editor ── */}
        <div style={s.editorWrapper}>
          {editorEmpty && (
            <span style={s.placeholder} aria-hidden="true">Write your message…</span>
          )}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="cm-editor"
            onInput={handleEditorInput}
            onKeyDown={(e) => {
              const mod = e.ctrlKey || e.metaKey;
              if (mod && !e.shiftKey && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                undoHistory();
              } else if (mod && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
                e.preventDefault();
                redoHistory();
              }
            }}
            onFocus={() => editorEmpty && setEditorEmpty(false)}
            onBlur={() => {
              const el = editorRef.current;
              if (!el) return;
              const userZone = el.querySelector('[data-compose-zone="user"]') || el;
              setEditorEmpty(userZone.innerText.trim() === '' && !userZone.querySelector('img, table'));
            }}
            style={s.editor}
          />
        </div>

      </div>

    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const s = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    background: C.bg,
    fontFamily: FONT,
    overflow: 'hidden',
  },

  actionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 16px',
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
    background: C.bg,
    minHeight: 52,
  },

  sendBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 34,
    padding: '0 14px',
    background: C.blue,
    color: '#fff',
    border: 'none',
    borderRadius: '4px 0 0 4px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: FONT,
    transition: 'background .15s',
    flexShrink: 0,
  },

  fromLabel: {
    fontSize: 13,
    color: C.textSec,
    fontFamily: FONT,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 340,
  },

  actionIconBtn: {
    width: 32, height: 32,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent', border: 'none', borderRadius: 4,
    color: C.textSec, cursor: 'pointer', fontSize: 16,
    transition: 'background .12s, color .12s',
  },

  fieldsArea: {
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
  },

  fieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${C.border}`,
    minHeight: 40,
  },

  fieldLabel: {
    fontSize: 13,
    color: C.textMuted,
    fontWeight: 500,
    padding: '10px 8px 0 16px',
    minWidth: 48,
    flexShrink: 0,
    fontFamily: FONT,
  },

  subjectInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 13,
    color: C.text,
    fontFamily: FONT,
    padding: '10px 14px',
    background: 'transparent',
    width: '100%',
    fontWeight: 400,
  },

  bccToggle: {
    alignSelf: 'center',
    background: 'none',
    border: 'none',
    color: C.blue,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 16px',
    fontFamily: FONT,
    flexShrink: 0,
  },

  attSection: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    padding: '6px 16px',
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
    background: '#faf9f8',
  },
  attChip: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 8px', border: `1px solid ${C.border}`,
    borderRadius: 4, background: C.bg, fontSize: 12, fontFamily: FONT,
  },
  attName: {
    maxWidth: 140, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.text,
  },
  attSize:   { color: C.textMuted, fontSize: 11 },
  attRemove: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 2, color: C.textMuted,
    display: 'flex', alignItems: 'center',
  },
  attTotal: { fontSize: 11, color: C.textMuted },

  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    fontSize: 11, color: C.textSec, fontFamily: FONT,
    background: '#f3f2f1', border: `1px solid ${C.border}`,
    borderRadius: 3, padding: '1px 6px',
  },

  // Editor area
  editorWrapper: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },

  placeholder: {
    position: 'absolute',
    top: 14,
    left: 20,
    color: '#bfbfbf',
    fontFamily: "'Calibri', 'Segoe UI', Arial, sans-serif",
    fontSize: 14,
    pointerEvents: 'none',
    userSelect: 'none',
    zIndex: 1,
  },

  editor: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '14px 20px',
    outline: 'none',
    fontFamily: "'Calibri', 'Segoe UI', Arial, sans-serif",
    fontSize: 14,
    lineHeight: 1.6,
    color: '#242424',
    wordBreak: 'break-word',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
  /* Send button */
  .cm-send-main:hover { background: #115ea3 !important; }

  /* Action icon buttons */
  .cm-action-icon:hover { background: #f3f2f1 !important; color: #0f6cbd !important; }
  .cm-discard:hover { color: #c50f1f !important; }

  /* Editor — preserve all pasted HTML (tables, inline styles, images) */
  .cm-editor { box-sizing: border-box; }
  .cm-editor:focus { outline: none; }
  .cm-editor table { border-collapse: collapse; }
  .cm-editor img { max-width: 100%; height: auto; }

  /* Lists — use native browser rendering */
  .cm-editor ul { list-style-type: disc;    padding-left: 1.5em; margin: 4px 0; }
  .cm-editor ol { list-style-type: decimal; padding-left: 1.5em; margin: 4px 0; }
  .cm-editor li { display: list-item; }

  /* History zone — non-editable, visually separated, cursor excluded */
  .cm-editor [data-compose-zone="history"] {
    user-select: text;        /* allow selecting/copying quoted content */
    pointer-events: none;     /* prevent click-to-focus inside zone */
    cursor: default;
    opacity: 0.85;
  }
  /* Re-enable pointer events for links inside history zone */
  .cm-editor [data-compose-zone="history"] a {
    pointer-events: auto;
    cursor: pointer;
  }
`;
